import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { Banknote, CreditCard, X, Wifi, ChevronLeft, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { paymentsAPI } from '../services/api';

// Internal component for the manual card entry form using Stripe Elements
const ManualCardForm = ({ orderId, onSuccess, onCancel, amount }: any) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        try {
            const res = await paymentsAPI.createIntent(orderId);
            const { clientSecret } = res.data;

            const cardElement = elements.getElement(CardElement);
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: { card: cardElement! }
            });

            if (error) {
                toast.error(error.message || 'Payment failed');
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                toast.success('Payment successful!');
                onSuccess('card_manual');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Payment error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div className="p-4 border-2 border-gray-200 rounded-xl focus-within:border-primary-400 transition-colors">
                <CardElement options={{
                    style: { base: { fontSize: '18px', color: '#32325d', '::placeholder': { color: '#aab7c4' } } }
                }} />
            </div>
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 flex items-center justify-center gap-2 py-4 text-base font-semibold border-2 border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
                    disabled={loading}
                >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                </button>
                <button
                    type="submit"
                    className="flex-1 py-4 text-base font-bold rounded-xl bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 transition-colors shadow-md"
                    disabled={loading || !stripe}
                >
                    {loading ? 'Processing...' : `Pay £${amount.toFixed(2)}`}
                </button>
            </div>
        </form>
    );
};

// Internal component for Stripe Terminal Contactless payments
const TerminalPayment = ({ orderId, onSuccess, onCancel, amount }: any) => {
    const [status, setStatus] = useState('Initializing Terminal...');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let terminal: any = null;

        const initializeTerminal = async () => {
            try {
                const StripeTerminal = await loadStripeTerminal();
                if (!StripeTerminal) throw new Error('Failed to load Stripe Terminal SDK');

                terminal = StripeTerminal.create({
                    onFetchConnectionToken: async () => {
                        const res = await paymentsAPI.createTerminalToken();
                        return res.data.secret;
                    },
                    onUnexpectedReaderDisconnect: () => {
                        setStatus('Reader disconnected unexpectedly.');
                    },
                });

                setStatus('Discovering simulated reader...');
                const discoverResult = await terminal.discoverReaders({
                    simulated: true,
                    discoveryMethod: 'simulated',
                });

                if (discoverResult.error) throw new Error(discoverResult.error.message);

                const simulatedReader = discoverResult.discoveredReaders[0];
                if (!simulatedReader) throw new Error('No simulated readers found');

                setStatus('Connecting to reader...');
                const connectResult = await terminal.connectReader(simulatedReader);
                if (connectResult.error) throw new Error(connectResult.error.message);

                setStatus('Creating payment intent...');
                const intentRes = await paymentsAPI.createTerminalIntent(orderId);
                const clientSecret = intentRes.data.clientSecret;

                setStatus('Waiting for tap (Simulated)...');
                setLoading(false);
                const collectResult = await terminal.collectPaymentMethod(clientSecret);
                if (collectResult.error) throw new Error(collectResult.error.message);

                setStatus('Processing payment...');
                setLoading(true);
                const processResult = await terminal.processPayment(collectResult.paymentIntent);
                if (processResult.error) throw new Error(processResult.error.message);

                setStatus('Capturing payment...');
                const captureRes = await paymentsAPI.captureTerminalPayment(processResult.paymentIntent.id);

                if (captureRes.data.status === 'succeeded') {
                    toast.success('Contactless payment successful!');
                    onSuccess('card_terminal');
                } else {
                    throw new Error('Capture failed');
                }
            } catch (err: any) {
                toast.error(err.message || 'Terminal error');
                setStatus(`Error: ${err.message || 'Terminal error'}`);
                setLoading(false);
            }
        };

        initializeTerminal();
        return () => { if (terminal) terminal.disconnectReader(); };
    }, [orderId, onSuccess]);

    return (
        <div className="mt-4 flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 text-center min-h-[200px]">
            <Wifi className={`w-14 h-14 mb-4 ${loading ? 'text-primary-500 animate-pulse' : 'text-gray-400'}`} />
            <p className="text-gray-700 font-semibold text-base">{status}</p>

            {!loading && status.includes('Waiting for tap') && (
                <p className="text-sm text-gray-500 mt-3 max-w-xs">
                    Simulated: in a real environment, the customer taps their card. The tap is auto-simulated by Stripe.
                </p>
            )}

            <button
                onClick={onCancel}
                className="mt-6 w-full flex items-center justify-center gap-2 py-4 text-base font-semibold border-2 border-gray-200 rounded-xl text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-40"
                disabled={loading && !status.includes('Error')}
            >
                <ChevronLeft className="w-5 h-5" />
                Cancel / Back
            </button>
        </div>
    );
};


interface PaymentModalProps {
    orderId: string;
    amount: number;
    onClose: () => void;
    onComplete: (paymentMethod: string) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ orderId, amount, onClose, onComplete }) => {
    const [selectedMethod, setSelectedMethod] = useState<'selection' | 'cash' | 'card_manual' | 'card_terminal'>('selection');
    const [stripePromise, setStripePromise] = useState<any>(null);

    useEffect(() => {
        paymentsAPI.getConfig().then((res: any) => {
            setStripePromise(loadStripe(res.data.publishableKey));
        }).catch((err: any) => {
            console.error('Failed to load Stripe config', err);
        });
    }, []);

    const handleCashSuccess = () => {
        toast.success('Cash payment recorded');
        onComplete('cash');
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Payment</h3>
                        <p className="text-2xl font-bold text-primary-600 mt-0.5">£{amount.toFixed(2)}</p>
                    </div>
                    {selectedMethod === 'selection' && (
                        <button
                            onClick={onClose}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </button>
                    )}
                </div>

                <div className="p-5">
                    {/* Payment method selection */}
                    {selectedMethod === 'selection' && (
                        <div className="space-y-3">
                            {/* Cash */}
                            <button
                                onClick={handleCashSuccess}
                                className="w-full flex items-center p-5 border-2 border-gray-100 rounded-xl hover:border-green-300 hover:bg-green-50 active:bg-green-100 transition-all"
                            >
                                <div className="bg-green-100 p-4 rounded-xl mr-4 flex-shrink-0">
                                    <Banknote className="w-7 h-7 text-green-600" />
                                </div>
                                <div className="text-left">
                                    <span className="block text-base font-bold text-gray-900">Cash Payment</span>
                                    <span className="block text-sm text-gray-500 mt-0.5">Record a cash transaction</span>
                                </div>
                            </button>

                            {/* Pay on Collection */}
                            <button
                                onClick={() => {
                                    toast.success('Pay on Collection selected');
                                    onComplete('pay_on_collection');
                                }}
                                className="w-full flex items-center p-5 border-2 border-gray-100 rounded-xl hover:border-orange-300 hover:bg-orange-50 active:bg-orange-100 transition-all"
                            >
                                <div className="bg-orange-100 p-4 rounded-xl mr-4 flex-shrink-0">
                                    <Clock className="w-7 h-7 text-orange-600" />
                                </div>
                                <div className="text-left">
                                    <span className="block text-base font-bold text-gray-900">Pay on Collection</span>
                                    <span className="block text-sm text-gray-500 mt-0.5">Customer will pay when collecting</span>
                                </div>
                            </button>

                            {/* Contactless Terminal */}
                            <button
                                onClick={() => setSelectedMethod('card_terminal')}
                                className="w-full flex items-center p-5 border-2 border-gray-100 rounded-xl hover:border-blue-300 hover:bg-blue-50 active:bg-blue-100 transition-all"
                            >
                                <div className="bg-blue-100 p-4 rounded-xl mr-4 flex-shrink-0">
                                    <Wifi className="w-7 h-7 text-blue-600" />
                                </div>
                                <div className="text-left">
                                    <span className="block text-base font-bold text-gray-900">Contactless (Terminal)</span>
                                    <span className="block text-sm text-gray-500 mt-0.5">Tap to pay with card or phone</span>
                                </div>
                            </button>

                            {/* Manual Card */}
                            <button
                                onClick={() => setSelectedMethod('card_manual')}
                                className="w-full flex items-center p-5 border-2 border-gray-100 rounded-xl hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-all"
                            >
                                <div className="bg-gray-100 p-4 rounded-xl mr-4 flex-shrink-0">
                                    <CreditCard className="w-7 h-7 text-gray-600" />
                                </div>
                                <div className="text-left">
                                    <span className="block text-base font-bold text-gray-900">Manual Card Entry</span>
                                    <span className="block text-sm text-gray-500 mt-0.5">Type in card details</span>
                                </div>
                            </button>
                        </div>
                    )}

                    {selectedMethod === 'card_manual' && stripePromise && (
                        <Elements stripe={stripePromise}>
                            <ManualCardForm
                                orderId={orderId}
                                amount={amount}
                                onSuccess={() => onComplete('card')}
                                onCancel={() => setSelectedMethod('selection')}
                            />
                        </Elements>
                    )}

                    {selectedMethod === 'card_manual' && !stripePromise && (
                        <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-3" />
                            <p>Loading payment gateway...</p>
                        </div>
                    )}

                    {selectedMethod === 'card_terminal' && (
                        <TerminalPayment
                            orderId={orderId}
                            amount={amount}
                            onSuccess={() => onComplete('card')}
                            onCancel={() => setSelectedMethod('selection')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
